import {
    defineStore,
    getActivePinia
} from "pinia";
import {
    AdminPortalUser,
    AdminUserStatusChangeRequest,
    AdminUserResponse,
    AdvisorEventType,
    AuthType,
    CommunicationPortal,
    EmployerAssociation,
    FeatureFlags,
    FetchState,
    FundingOption, HsabEnrollmentSettingsResponse,
    IdpStatusChangeRequest,
    InvestmentCustodian,
    ResyncTalonClaimsRequest,
    LoginMethod,
    MfaStatusEnum,
    PartnerAssociation,
    PasswordChangeRequest,
    SearchAdminUsers,
    SearchResultUser,
    User,
    UserDetails,
    UserPartnerSettings,
    UserRole,
    UserServiceAccountType,
    UserState,
    UserRoleInt,
    VerifyIdpEmailChangeRequest,
    UserTargeting,
} from "@/shared/types";
import {
    hasRevealAccess,
    isAdmin
} from "@/shared/groups";
import { AuthFactory } from "@/shared/api/auth";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { Config } from "@/shared/config";
import _ from "lodash";
import Vue from "vue";
import {
    Communication,
    PartnerEmployer
} from "@/employer/types";
import { downloadCsvFile } from "@/shared/fileDownload";
import { CognitoUser } from "amazon-cognito-identity-js";
import { persistedKeys } from "@/shared/localstoragekeys";
import { detectDevice } from "@/shared/deviceDetect";
import { useNotifierStore } from "./notifier";
import { useEnrollmentStore } from "@/employee/store/enrollment";
import { useMfaStore } from "./mfa";
import { useThemeStore } from "./theme";
import { useFeatureFlagStore } from "./featureFlag";
import { useRolePermissionsStore } from "./rolePermissions";
import { useWizardStore } from "@/employee/store/wizard";
import { usePortalCustomizationStore } from "@/shared/store/portalCustomization";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useUserPlanStore } from "@/employee/store/userPlan";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useEbtoStore } from "@/shared/store/ebto";
import { useHsabInvestmentStore } from "@/shared/store/hsabInvestment";
import { useUserAdvisorStore } from "@/shared/store/userAdvisor";
import { useImpersonatorStore } from "@/shared/store/impersonator";
import { useSsoStore } from "@/employee/store/sso";
import { useUserFeedbackStore } from "@/employee/store/userFeedback";
import { useRootStore } from "@/shared/store/root";
import { LoginRouteNames } from "@/shared/loginRoutes";
import { useHsabExpenseStore } from "@/employee/store/hsabExpense";
import { useInvestmentThresholdStore } from "@/shared/store/investmentThreshold";
import { useRoleStore } from "@/shared/store/role";

const preferredLogoutInactivityTimeSeconds = 15 /* fifteen minutes */ * 60;

function isActivityTimeout (lastActivity: number) {
    const inactivityDurationSeconds = ((new Date()).valueOf() - lastActivity) / 1000;
    return inactivityDurationSeconds > preferredLogoutInactivityTimeSeconds;
}

const isAutoLogoutDisabledForEnvironment = (): boolean => {
    if (window.location.toString().indexOf("/sso-validate") >= 0 || window.location.toString().indexOf("/auth/sso") >= 0) {
        return true;
    }

    const isUat = Config.UserServicebaseUri.indexOf("https://api-uat.live-test-domain.com") >= 0;
    const isProduction = Config.UserServicebaseUri.indexOf("https://api.bendhsa.com") >= 0;

    return !isUat && !isProduction;
};

const cleanupLocalStorageValues = () => {
    let valuesToPersist = [];
    persistedKeys.map(key => {
        const v = window.localStorage.getItem(key);
        if (v !== undefined && v !== null) {
            valuesToPersist = [ ...valuesToPersist, { key, value: v } ];
        }
    });

    window.localStorage.clear();

    // restore
    valuesToPersist.forEach(kv => {
        window.localStorage.setItem(kv.key, kv.value);
    });
};

export interface LoginParam {
    username: string;
    password?: string;
    verificationCode?: string;
    logoutOnStart?: boolean;
    delayAfterLogout?: boolean;
}

const emptyUser: User = {
    userId: null,
    displayName: "",
    email: "",
    groups: [],
    firstName: "",
    lastName: "",
    phone: "",
    username: ""
};

export const initialState: UserState = {
    user: _.cloneDeep(emptyUser),
    bendUser: _.cloneDeep(emptyUser),
    employersState: [],
    partners: [],
    onBoardingProgress: null,
    employerId: 0,
    partnerId: 0,
    isNotionalDashboardActive: false,
    loggedIn: false,
    userList: [],
    userListState: FetchState.Uninitialized,
    validGroups: [],
    noAccess: false,
    loginUrl: "https://user.bendhsa.com/",
    site: "",
    cognitoDR: false,
    checkValidGroups: true,
    lastActivity: undefined,
    accountType: UserServiceAccountType.None,
    employeeFundingOption: FundingOption.None,
    canEmployerUpdateCensus: null,
    partnerSettingsState: FetchState.Uninitialized,
    partnerUsesCustomClaims: null,
    partnerClaimsOptIn: false,
    partnerDisableTalon: false,
    partnerConsumerLiveChatEnabled: false,
    partnerIsEnrollmentDisabled: false,
    partnerDisableInvestmentActivity: false,
    communications: new Map<CommunicationPortal, Communication[]>([ [ CommunicationPortal.Employer, [] ],
        [ CommunicationPortal.Employee, [] ] ]),
    communicationsFetchState: FetchState.Uninitialized,
    isAutoEnrolled: false,
    authType: AuthType.Cognito,
    accountHolderPartnerId: 0,
    accountHolderEmployerId: 0,
    loginMethod: LoginMethod.Unknown,
    contactEmail: null,
    adminPortalUsers: null,
    adminPortalUserResult: null,
    adminPortalUserSearchFetchState: FetchState.Uninitialized,
    currentAdminPortalUser : null,
};

export const useUserStore = defineStore("user", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("user"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        siteTabsTheme () {
            return this.isEmployeePortal ? "employee" : "employer";
        },
        currentUser: (state): User => state.user,
        currentBendUser: (state): User => state.bendUser,
        hasRevealAccess: state => hasRevealAccess(state.user),
        isAdmin: state => isAdmin(state.user),
        currentEmployerName () {
            const employers = this.currentEmployer;
            if (employers) {
                return employers.employerName;
            }
            return "";
        },
        userId: state => state.user.userId,
        employers: state => state.employersState.filter(employer => !employer.isAccessBasedEmployer),
        currentEmployer: state => state.employersState.find(association => association.employerId === state.employerId),
        partnersWithUserRoles: state => _.filter(state.partners, p => p.partnerUserRoles.length > 0),
        currentPartner: state => state.partners.find(association => association.partnerId === state.partnerId),
        noAccessLoginUrl: state => state.noAccess && state.loginUrl ? state.loginUrl : "https://user.bendhsa.com/",
        email: state => (state.user && state.loggedIn) ? state.user.email : null,
        displayName: state => (state.user && state.loggedIn) ? state.user.displayName : null,
        firstName: state => (state.user && state.loggedIn) ? state.user.firstName : null,
        phone: state => (state.user && state.loggedIn) ? state.user.phone : null,
        username: state => (state.user && state.loggedIn) ? state.user.username : null,
        employerOnboarded () {
            const employer = this.currentEmployer;
            if (employer && employer.onBoardingProgress) {
                return employer.onBoardingProgress.wizardComplete;
            }
            return null;
        },
        employerOnboardingProgress () {
            const employer = this.currentEmployer;
            if (employer && employer.onBoardingProgress) {
                return employer.onBoardingProgress;
            }
            return null;
        },
        isAdminPortal: state =>  state.site === "ADMIN",
        isEmployeePortal: state => state.site === "EMPLOYEE",
        isEmployerPortal: state => state.site === "EMPLOYER",
        userOnboarded: state => {
            if (state.onBoardingProgress) {
                return state.onBoardingProgress.wizardComplete;
            }
            return null;
        },
        userOnboardingProgress: state => {
            if (state.onBoardingProgress) {
                return state.onBoardingProgress;
            }
            return null;
        },
        isEmployeeAccountType: state => {
            return state.accountType === UserServiceAccountType.Employee;
        },
        isIndividualAccountType: state => {
            return state.accountType === UserServiceAccountType.Individual;
        },
        hasAdminAccess: state => _.includes(state.validGroups, "admin"),
        hasPartnerOwnerRole () {
            if (this.site === "ADMIN") {
                return true;
            }
            const currentPartner = this.currentPartner;

            if (currentPartner && currentPartner.partnerUserRoles) {
              return _.includes(currentPartner.partnerUserRoles, UserRoleInt.partnerAdmin);
            }

            return false;
        },
        groupsForLogin: state => state.checkValidGroups ? state.validGroups : [],
        claimsOptIn: state => state.partnerClaimsOptIn,
        allAdminUsers: state => state.adminPortalUsers,
        adminSearchResults: state => state.adminPortalUserResult,
        adminSearchIsLoading: state  => state.adminPortalUserSearchFetchState === FetchState.Loading,
        currentAdminUser: state => state.currentAdminPortalUser,
        passwordMinLength: state => state.site === "ADMIN" ? 16 : 12
    },
    actions: {
        setEmployerId (employerId: number) {
            this.employerId = employerId;
            const featureFlagStore = useFeatureFlagStore();
            featureFlagStore.setUserContext({ employerId });
            if (this.isEmployerPortal) {
              const portalCustomization = usePortalCustomizationStore();
              portalCustomization.getSupportInfo(
                { partnerId: portalCustomization.partnerId, employerId: this.employerId } );
            }
        },
        setUserDetail (userDetail: UserDetails) {
            // userdetail values are coming from the user service api
            this.user.userId = userDetail.userId;
            this.contactEmail = userDetail.email;
            this.user.firstName = userDetail.firstName;
            this.user.lastName = userDetail.lastName;
            this.user.phone = userDetail.phone;
            this.user.displayName = `${userDetail.firstName} ${userDetail.lastName}`;
            this.accountType = userDetail.accountType;
            this.employeeFundingOption = userDetail.employeeFundingOption ? userDetail.employeeFundingOption
                : FundingOption.None;
            this.isAutoEnrolled = userDetail.isAutoEnrolled;
            this.user.hashkey = userDetail.five9Hash;
            this.user.campaignKey = userDetail.five9CampaignKey;
        },
        setBendUserDetail (userDetail: UserDetails) {
            // userdetail values are coming from the user service api
            this.bendUser.email = userDetail.email;
            this.bendUser.firstName = userDetail.firstName;
            this.bendUser.lastName = userDetail.lastName;
            this.bendUser.phone = userDetail.phone;
            this.bendUser.displayName = `${userDetail.firstName} ${userDetail.lastName}`;
            this.bendUser.hashkey = userDetail.five9Hash;
            this.bendUser.campaignKey = userDetail.five9CampaignKey;
            this.bendUser.chatDelay = userDetail.chatDelay;
        },
        setUser (user: User) {
            // user values are coming from aws cognito sdk
            this.user.email = user.email;
            this.user.displayName = `${user.firstName} ${user.lastName}`;
            this.user.groups = user.groups;
            this.user.phone = user.phone;
            this.user.username = user.username;
        },
        setLoginUsername (userName: string) {
            return this.user.username = userName;
        },
        setLoggedIn (isLoggedIn: boolean) {
            this.loggedIn = isLoggedIn;
        },
        loginEvent (skipSync) {
            if (skipSync) {
                return;
            }
            return UserService.userLoginEvent();
        },
        removeUser (args: { logout: boolean }) {
            this.setEmployerId(0);
            const featureFlagStore = useFeatureFlagStore();
            featureFlagStore.setUserContext(null);
            this.loggedIn = false;
            this.user = _.cloneDeep(emptyUser);
            this.noAccess = false;
            this.cognitoDR = false;

            if (args.logout) {
                AuthFactory.getAuth().logout();
            }
        },
        addAccessBasedEmployers (employers: PartnerEmployer[]) {
            if (!this.employersState) {
                this.employersState = [];
            }

            const map: {[key: number]: EmployerAssociation} = {};

            for (const employer of (this.employersState as EmployerAssociation[])) {
                map[employer.employerId] = employer;
            }

            for (const employer of employers) {
                if (map[employer.employerId]) {
                    // if the isTerminated flag is different than the existing one, update the state
                    // continue without a call to Vue.set if unchanged
                    // look further into the persisted state of when this mutation is called and with what data
                    // because it's seems crazy
                    if (employer.isTerminated !== map[employer.employerId].isTerminated) {
                        const index = this.employersState.findIndex(
                            (e: EmployerAssociation) => e.employerId === employer.employerId);
                        Vue.set(this.employersState, index, {
                            ...map[employer.employerId],
                            isTerminated: employer.isTerminated
                        });
                    }
                    // SET current employer's properties
                    // After saving employer edit screen, partner employers gets reloaded
                    if (employer.employerId === this.employerId) {
                        const index = this.employersState.findIndex(
                            (e: EmployerAssociation) => e.employerId === employer.employerId);
                        Vue.set(this.employersState, index, {
                            ...map[employer.employerId],
                            fundingOption: employer.fundingOption,
                            hasFundingOptionHistory: employer.hasFundingOptionHistory
                        });
                    }

                    continue;
                }

                this.employersState.push({
                    employerId: employer.employerId,
                    employerName: employer.name,
                    fundingOption: employer.fundingOption,
                    hasFundingOptionHistory: employer.hasFundingOptionHistory,
                    censusManager: employer.censusManager,
                    isTerminated: employer.isTerminated,
                    onBoardingProgress: {
                        currentStep: 4,
                        wizardComplete: true
                    },
                    isAccessBasedEmployer: true,
                    partnerId: employer.partnerId,
                    employerUserRoles: []
                });
            }
        },
        setEmployers (employerAssociations: EmployerAssociation[]) {
            this.employersState = employerAssociations;
            if (this.employersState.length > 0) {
                this.setEmployerId(employerAssociations[0].employerId);
            }
        },
        setAccountHolderPartnerId (partnerId: number) {
            this.accountHolderPartnerId = partnerId;
            if (this.isEmployeePortal) {
              const portalCustomization = usePortalCustomizationStore();
              portalCustomization.getSupportInfo(
                { partnerId: this.accountHolderPartnerId, employerId: this.accountHolderEmployerId} );
            }
        },
        setAccountHolderEmployerId (employerId: number) {
            this.accountHolderEmployerId = employerId;
            if (this.isEmployeePortal) {
              const portalCustomization = usePortalCustomizationStore();
              portalCustomization.getSupportInfo(
                { partnerId: this.accountHolderPartnerId, employerId: this.accountHolderEmployerId} );
            }
        },
        clearPartnerId () {
            this.partnerId = 0;
            const featureFlagStore = useFeatureFlagStore();
            featureFlagStore.setUserContext({ partnerId: 0 });
        },
        setPartnerId (partnerId: number) {
            if (!this.partners || !this.partners.length) {
                return;
            }

            if (this.partners.find((association: PartnerAssociation) => association.partnerId === partnerId)) {
                this.partnerId = partnerId;
                const featureFlagStore = useFeatureFlagStore();
                featureFlagStore.setUserContext({ partnerId });
            }
        },
        setPartners (partnerAssociations: PartnerAssociation[]) {
            this.partners = partnerAssociations;
            if (this.partnerId === 0 && this.partners && this.partners.length) {
                this.partnerId = this.partners[0].partnerId;
            }
        },
        setUserList (userList: SearchResultUser[]) {
            this.userList = userList;
            this.userListState = this.Loaded;
        },
        setUserListLoading () {
            this.userListState = FetchState.Loading;
        },
        setValidGroups (groups: string[]) {
            this.validGroups = [ ...groups ];
        },
        setNoAccess (noAccess: boolean) {
            this.noAccess = noAccess;
        },
        setNoAccessLoginUrl (noAccessLoginUrl: string) {
            this.loginUrl = noAccessLoginUrl;
        },
        setSite (site: string) {
            this.site = site;
        },
        setEmployerOnboarded () {
            const currentEmployer = this.employersState
                .find((association: EmployerAssociation) => association.employerId === this.employerId);
            if (currentEmployer) {
                // in the context of partner creating new employer
                // this current employer is not here
                currentEmployer.onBoardingProgress.wizardComplete = true;
            }
        },
        setActiveUserPing () {
            this.lastActivity = (new Date()).valueOf();
        },
        setCognitoDisasterRecovery (email: string) {
            this.cognitoDR = true;
            this.user = { ..._.cloneDeep(emptyUser), email };
        },
        setCheckValidGroups (value: boolean) {
            this.checkValidGroups = value;
        },
        setEmployerUpdateCensusSetting (employerUpdateCensusSetting: boolean) {
            this.canEmployerUpdateCensus = employerUpdateCensusSetting;
        },
        setPartnerSettings (settings: UserPartnerSettings) {
            this.partnerUsesCustomClaims = settings.partnerUsesCustomClaims;
            this.partnerClaimsOptIn = settings.claimsOptIn;
            this.partnerDisableTalon = settings.disableTalon;
            this.partnerConsumerLiveChatEnabled = settings.consumerLiveChatEnabled;
            this.partnerIsEnrollmentDisabled = settings.isEnrollmentDisabled;
            this.partnerDisableInvestmentActivity = settings.disableInvestmentActivity;
        },
        setPartnerSettingsLoaded () {
            this.partnerSettingsState = FetchState.Loaded;
        },
        addCommunication (data: { portal: CommunicationPortal, value: Communication }) {
            this.communications[data.portal].push(data.value);
        },
        setCommunications (data: { portal: CommunicationPortal, communications: Communication[] }) {
            this.communications[data.portal] = [ ...data.communications ];
        },
        setCommunicationsFetchState (fetchState: FetchState) {
            this.communicationsFetchState = fetchState;
        },
        setAuthType (authType: AuthType) {
            this.authType = authType;
        },
        setIsNotionalDashboardActive (isNotionalDashboardActive: boolean) {
            this.isNotionalDashboardActive = isNotionalDashboardActive;
        },
        setLoginMethod (loginMethod: LoginMethod) {
            this.loginMethod = loginMethod;
        },
        create (request: UserService.CreateUserRequest): Promise<any> {
            const requestWithCurrentEmployer = { employerId: this.employerId, ...request };
            return UserService.createUser(requestWithCurrentEmployer)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        activeUserPing () {
            this.checkActivityForAutoLogout()
                .then(() => this.setActiveUserPing());
        },
        startAutologoutTimer () {
            this.checkActivityForAutoLogout();
            if (isAutoLogoutDisabledForEnvironment()) return;
            if (this.intervalId) return;
            if (!this.loggedIn) return;
            this.intervalId = window.setInterval(() => {
                this.checkActivityForAutoLogout();
            }, 30000);
        },
        checkActivityForAutoLogout () {
            if (!this.lastActivity) {
                this.setActiveUserPing();
            }

            let lastActivity = this.lastActivity;

            // the following code is used to prevent multiple open browser tabs from timing each other out
            const storage = window.localStorage;
            if (storage && storage.user) {
                try {
                    const actualState = JSON.parse(storage.user) as UserState;
                    const actualLastActivity = _.get(actualState, "lastActivity", null) as number;

                    if (_.isFinite(actualLastActivity) && actualLastActivity > lastActivity) {
                        lastActivity = actualLastActivity;
                    }
                } catch (e) {
                    window.console.log("Could not parse pinia state from localStorage", e);
                }
            }

            if (isNaN(lastActivity) || isActivityTimeout(lastActivity)) {
                const ebtoStore = useEbtoStore();
                ebtoStore.beginAutoLogout();
            }

            if (!this.loggedIn || isAutoLogoutDisabledForEnvironment()) {
                return Promise.resolve();
            }

            if (isNaN(lastActivity) || isActivityTimeout(lastActivity)) {
                this.logout();
            }

            return Promise.resolve();
        },
        resetPassword ({ username, authType }): Promise<void> {
            const notifierMessage = "You will receive an email shortly containing a password reset verification code.";
            const loginUsername = username.toLowerCase();
            const notifierStore = useNotifierStore();
            return AuthFactory.getAuthByType(authType).resetPassword(loginUsername)
                .then(() => notifierStore.setSuccess(notifierMessage))
                .catch(err => {
                    return this.autoRecoverUser({ emailAddress: loginUsername, originalRejection: err });
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        changePassword (request: PasswordChangeRequest) {
            const notifierStore = useNotifierStore();
            return AuthFactory.getAuth().changePassword(request)
                .then(() => {
                    notifierStore.setSuccess("Your password was successfully updated.");
                })
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        login (loginParam: LoginParam): Promise<void> {
            this.removeUser({ logout: loginParam.logoutOnStart });
            const requiredGroupsForLogin = this.checkValidGroups ? this.validGroups : [];
            let afterPromise = Promise.resolve();
            const enrollmentStore = useEnrollmentStore();
            if (this.site === "EMPLOYEE" && loginParam.verificationCode) {
                afterPromise = enrollmentStore.addEmployeeGroupToCognitoUser({
                    email: loginParam.username,
                    verificationCode: loginParam.verificationCode
                });
            }

            // For claim account, AWS Cognito call to logout is not always finished revoking the tokens
            // by the time the login/verify credentials are reached. For this case, manually delay 1000 ms
            // Bug ticket: https://bendhsa.atlassian.net/browse/BEND-196022
            // StackOverflow explanation: https://stackoverflow.com/questions/56468808/adminuserglobalsignout-not-returning-any-data-or-not-awaiting-promise

            const delayInMs = loginParam.delayAfterLogout ? 1000 : 0;
            const awaitTimeout = delay => new Promise(resolve => setTimeout(resolve, delay));

            return awaitTimeout(delayInMs).then(() => {
                return afterPromise.then(_ =>
                    AuthFactory.getAuth()
                        .login(loginParam.username.toLowerCase(), loginParam.password, requiredGroupsForLogin,
                            (s: MfaStatusEnum, u: CognitoUser) => {
                                const mfaStore = useMfaStore();
                                mfaStore.setMfaStatus(s);
                                mfaStore.setCognitoUser(u);
                            })
                        .then((data: User) => {
                            if (!data) {// 2fa
                                return Promise.resolve();
                            }
                            return this.processLogin(data);
                        })
                        .catch(err => {
                            if (err.toString().indexOf("not auth") > 0) {
                                return Promise.resolve();
                            }
                            if (err && err.missingRequiredGroup) {
                                this.setNoAccess(true);
                                this.setNoAccessLoginUrl(err.loginUrl);
                            }

                            return this.autoRecoverUser({
                                    emailAddress: loginParam.username.toLowerCase(),
                                    originalRejection: err
                            });
                        }));
            });
        },
        postLogin (): Promise<void> {
            this.removeUser({ logout: false});
            const requiredGroupsForLogin = this.checkValidGroups ? this.validGroups : [];
            return AuthFactory.getAuth()
                .postLoginProcess(requiredGroupsForLogin)
                .then((data: User) => this.processLogin(data));
        },
        processLogin (user: User | {
            email: string,
            displayName: string,
            groups: string[],
            phone: string
        }) {
            this.setUser(user);

            const rolePermissionsStore = useRolePermissionsStore();
            const mfaStore = useMfaStore();
            const featureFlagStore = useFeatureFlagStore();
            const themeStore = useThemeStore();
            const wizardStore = useWizardStore();
            const portalCustomizationStore = usePortalCustomizationStore();
            const userPlanStore = useUserPlanStore();
            const hsabAccountsStore = useHsabAccountsStore();
            const custodianSettingsStore = useCustodianSettingsStore();
            const hsabInvestmentStore = useHsabInvestmentStore();
            const userAdvisorStore = useUserAdvisorStore();
            const impersonatorStore = useImpersonatorStore();
            const investmentThresholdStore = useInvestmentThresholdStore();
            const roleStore = useRoleStore();

            if (this.site === "ADMIN") {
                const userPermission = rolePermissionsStore.getUserPermissions();
                userPermission.then(_ => {
                    this.setLoggedIn(true);
                    this.startAutologoutTimer();
                    return;
                });
            }

            const permissionPromise = (this.site === "EMPLOYER") ?
                rolePermissionsStore.getRolePermissions()
                : Promise.resolve();

            const isInitialLogin = this.loggedIn === false;

            return permissionPromise.then(_ => UserService.getUser())
                .then(response => {
                    // get roles to be used in validation
                    roleStore.getPartnerRoles();
                    roleStore.getEmployerRoles();

                    const userDetail = response.data;
                    this.setUserDetail(userDetail);
                    this.setBendUserDetail(userDetail);
                    mfaStore.setShowMfa(userDetail.showMfa);
                    const ldContextUpdate: Partial<UserTargeting> = { email: this.user.email };
                    if (userDetail.employer) {
                        ldContextUpdate.employerId = userDetail.employer.employerId;
                        ldContextUpdate.custodian = userDetail.employer.custodian;
                        this.setAccountHolderEmployerId(userDetail.employer.employerId);
                    }
                    if (userDetail.employeePartnerId) {
                        ldContextUpdate.partnerId = userDetail.employeePartnerId;
                        portalCustomizationStore.getEmployeeLogoutRedirectUrl(userDetail.employeePartnerId);
                        this.setAccountHolderPartnerId(userDetail.employeePartnerId);
                    }
                    const featureFlagStore = useFeatureFlagStore();
                    featureFlagStore.setUserContext(ldContextUpdate);
                    return userDetail;
                })
                .then(userDetail => {
                    const site: string = this.site;
                    let loginPartnerId = 0;

                    if (userDetail.userPartners.length) {
                        this.setPartners(userDetail.userPartners);
                    }

                    if (site === "EMPLOYER") {
                        if (userDetail.userEmployers.length) {
                            this.setEmployers(userDetail.userEmployers);
                        }

                        if (this.employerId) {
                            // Employer customization
                            const employerPartnerId = this.employersState.find(
                                (ers: EmployerAssociation) => ers.employerId === this.employerId).partnerId;
                            this.portalCustomization(employerPartnerId);
                        } else {
                            // Partner customization
                            this.portalCustomization(this.partnerId);
                        }

                        if (!userDetail.isConfirmed) {
                            UserService.confirmUser()
                              .catch(() => {/* error is logged on the backend, no need to notify user */});
                        }
                    }

                    if (site === "EMPLOYEE") {
                        loginPartnerId = userDetail.employeePartnerId;
                        // these modules are not registered in every portal
                        this.portalCustomization(userDetail.employeePartnerId);
                        userPlanStore.setUserPlanType(userDetail.planType);
                        themeStore.getTheme(
                            {
                                employerId: this.accountHolderEmployerId,
                                partnerId: userDetail.employeePartnerId,
                                forceUpdate: true
                            });
                    }

                    return { loginPartnerId };
                })
                .then(_ => {
                    if (this.authType === AuthType.Okta) {
                        return hsabAccountsStore.getHsabAccounts();
                    } else {
                        return Promise.resolve();
                    }
                })
                .then(_ => {
                    if (this.site === "EMPLOYER" && this.employerId) {
                        return custodianSettingsStore.getEmployerCustodian(null)
                            .then(_ => {
                                this.setLoggedIn(true);
                                this.startAutologoutTimer();
                            });
                    } else if (this.site === "EMPLOYEE") {
                        return custodianSettingsStore.getCustodianSettings({
                                loginPartnerId: this.accountHolderPartnerId,
                                force: true
                            })
                            .then(() => {
                                return wizardStore.getWizardState({force: true});
                            })
                            .then(() => {
                                this.setLoggedIn(true);
                                this.startAutologoutTimer();
                                this.saveAdviserLoginAttribute();
                            });
                    } else {
                        this.setLoggedIn(true);
                        this.startAutologoutTimer();
                    }
                }).then(() => {
                  if (this.site === "EMPLOYEE" &&
                      custodianSettingsStore.isHsaBankCustodian) {
                        this.loginEvent(!isInitialLogin);
                  }
                  return Promise.resolve();
                }).then(() => {
                    if (this.site === "EMPLOYEE") {
                        if (impersonatorStore.impersonationIsStarting === false && isInitialLogin) {
                            const appInfo = detectDevice();

                            if (appInfo.isMobile) {
                                const userFeedbackStore = useUserFeedbackStore();
                                userFeedbackStore.getUserFeedbackTriggers();
                            }

                            return UserService.logEmployeeLogin({
                                platform: appInfo.platform,
                                application: appInfo.name,
                                appVersion: appInfo.version,
                                isMobile: appInfo.isMobile,
                                loginMethod: this.loginMethod,
                            });
                        }
                    }
                });
        },

        autoRecoverUser (actionParam: { emailAddress: string, originalRejection: any }) {
            actionParam.emailAddress = actionParam.emailAddress.toLowerCase();
            const originalRejection = actionParam.originalRejection;
            const isMissingUser = originalRejection && originalRejection.code === "UserNotFoundException";
            if (isMissingUser && Config.CognitoDRMode === true) {
                return AuthFactory.getAuth().autoRecoverUser(actionParam.emailAddress)
                    .then(_ => {
                        this.setCognitoDisasterRecovery(actionParam.emailAddress); // user was recovered
                    })
                    .catch(_ => {
                        throw originalRejection;
                    });
            }
            return Promise.reject(originalRejection);
        },
        resyncTalonClaims (r: ResyncTalonClaimsRequest): Promise<any> {
          return UserService.resyncTalonClaims(r)
            .then(_ => {
              const notifierStore = useNotifierStore();
              notifierStore.setSuccess(`My Medical Shopper re-sync has been queued for processing.`, 4000);
            })
            .catch(err => {
              storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 });
            });
        },
        refreshUser () {
            const impersonator = useImpersonatorStore();
            if (impersonator.isActive) {
                // if we are impersonating, we don't have a session established with the auth provider so refreshSession won't work
                this.processLogin(this.user);
                return Promise.resolve();
            }

            return AuthFactory.getAuth().forceRefreshSession()
                .then(user => this.processLogin(user));
        },

        disableRequiredGroups () {
            this.setCheckValidGroups(false);
        },

        logout () {
            const custodianSettingsStore = useCustodianSettingsStore();
            const portalCustomizationStore = usePortalCustomizationStore();
            const impersonatorStore = useImpersonatorStore();
            const ssoStore = useSsoStore();
            const logoutRedirectUrl = custodianSettingsStore.logoutRedirectUrl;
            const isSsoLogin = ssoStore.isLoggingIn;
            const partnerLogoutRedirectUrl = portalCustomizationStore.employeeLogoutRedirectUrl;
            const authType = this.authType;
            const shouldLogout = impersonatorStore.isActive ? false : true;

            this.removeUser({ logout: shouldLogout });

            // Redirect user to appropriate location
            if (logoutRedirectUrl !== null) {
                window.location.replace(logoutRedirectUrl);
            } else if (partnerLogoutRedirectUrl) {
                window.location.replace(partnerLogoutRedirectUrl);
            } else if (!isSsoLogin) {
                // Redirect logic is not required for okta employer portal here. Is is handled on LoginDecider.vue
                if (authType === AuthType.Okta && !this.isEmployerPortal) {
                    if (this.isAdminPortal) {
                        if (this.router.currentRoute.name !== LoginRouteNames.AdminOktaLogin) {
                            this.router.push({ name: LoginRouteNames.AdminOktaLogin });
                        }
                    } else if (this.router.currentRoute.name !== LoginRouteNames.OktaLogin) {
                        this.router.push({ name: LoginRouteNames.OktaLogin });
                    }
                } else if (this.router.currentRoute.name !== LoginRouteNames.Login) {
                    this.router.push({ name: LoginRouteNames.Login });
                }
            }

            portalCustomizationStore.resetEmployerLogo();

            // Reset current state since we did not navigate offsite
            const rootState = useRootStore();
            rootState.resetState();
            this.setAuthType(authType);

            return impersonatorStore.stopImpersonation()
                .then(() => {
                    cleanupLocalStorageValues();
                });
        },

        submitVerificationCode (args: {
            username: string,
            verificationCode: string,
            password: string
        }): Promise<void> {
            const { username, verificationCode, password } = args;
            return AuthFactory.getAuth().confirmPassword(username, verificationCode, password)
                .then(() => this.login({ username, password }));
        },

        setInitialPassword (args: { username: string, originalPassword: string, newPassword: string }):
            Promise<void> {
            const { username, originalPassword, newPassword } = args;
            let mfaStatus = MfaStatusEnum.Disabled;
            const mfaStore = useMfaStore();
            return AuthFactory.getAuth()
                .setInitialPassword(username.toLowerCase(), originalPassword, newPassword,
                    (s: MfaStatusEnum, u: CognitoUser) => {
                        mfaStatus = s;
                        mfaStore.setMfaStatus(s);
                        mfaStore.setCognitoUser(u);
                    })
                .then(_ => {
                    if (mfaStatus === MfaStatusEnum.Disabled) {
                        this.login({ username, password: newPassword });
                    }
                });
        },

        listAllUsers (): Promise<SearchResultUser[]> {
            if (this.userListState === FetchState.Loaded) {
                return Promise.resolve(this.userList);
            }
            this.setUserListLoading();
            return new Promise<SearchResultUser[]>((resolve, reject) => {
                UserService.searchForUserByEmail("").then(response => {
                    if (!response) {
                        reject({ message: "No response from the User api at all", payload: response });
                        return;
                    }

                    if (response.status !== 200) {
                        reject({ message: "User api returned non-OK status", payload: response });
                        return;
                    }

                    resolve(response.data);
                });
            })
                .then(employerUsers => {
                    this.setUserList(employerUsers);
                    return employerUsers;
                });
        },
        changeActiveEmployer ({ employerId }: { employerId: number }) {
            if (employerId === this.employerId) {
                return;
            }
            const employer = this.employersState.find((e: EmployerAssociation) => e.employerId === employerId);
            let partnerId = null;
            if (employer) {
                partnerId = employer.partnerId;
            }
            this.portalCustomization(partnerId);

            // reset setting to default value
            this.setEmployerUpdateCensusSetting(null);
            const activepinia = getActivePinia();
            const excludedModules = [ "user", "portalCustomization", "partner", "pageSize", "rolePermissions", "featureFlag" ];
            const moduleNotInExcludedModules = (v: string) => !excludedModules.some(
                excludedModuleName => excludedModuleName === v);
            const moduleKeys = Object.keys(activepinia.state.value).filter(moduleNotInExcludedModules);

            this.setEmployerId(employerId);
            const rootStore = useRootStore();
            rootStore.filteredStateReset({ moduleKeys });
            UserService.logEmployerAccess(employerId, partnerId);
        },
        changePartner (partnerId: number) {
            if (this.partnerId === partnerId) {
                return;
            }
            this.setPartnerId(partnerId);
            this.setEmployerId(null);
            this.portalCustomization(partnerId);
            UserService.logEmployerAccess(null, partnerId);
        },
        portalCustomization (partnerId?: number) {
            const portalCustomizationStore = usePortalCustomizationStore();
            if (partnerId) {
                portalCustomizationStore.getPartnerCustomLogo(partnerId);
                portalCustomizationStore.getCustomDebitCardImage(partnerId);
                portalCustomizationStore.getCommunicationKitType(partnerId);
            } else {
                portalCustomizationStore.resetCustomization();
            }
        },
        saveAdviserLoginAttribute () {
            const userAdvisorStore = useUserAdvisorStore();
            if (this.site) {
                setTimeout(() => {
                    userAdvisorStore.saveEvent(AdvisorEventType.Login);
                }, 1000);

            }
        },
        async downloadFraudPreventionReport () {
            return UserService.downloadFraudPreventionReport()
                .then(response => {
                    const fileName = "Fraud-Prevention-Report.csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        downloadDemoUsersReport () {
            return UserService.downloadDemoUsersReport()
                .then(response => {
                    const fileName = "Demo-Users-Report.csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadBirthYear (): Promise<any> {
            this.setBirthYearFetchState(FetchState.Loading);
            return UserService.getBirthYear().then(response => {
                this.setBirthYear(response.data.birthYear);
                this.setBirthYearFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: false });
            });
        },
        sendIndividualSignupEmailClick (username: string): Promise<any> {
            return UserService.sendIndividualSignupEmailClick(username);
        },
        getEmployerUpdateCensusSetting (employerId: number) {
            if (this.canEmployerUpdateCensus !== null) {
                return;
            }

            UserService.employerUpdateCensusSetting(employerId).then(response => {
                this.setEmployerUpdateCensusSetting(response.data);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: false });
            });
        },
        loadPartnerSettings () {
            if (this.partnerSettingsState === FetchState.Loaded) {
                return Promise.resolve();
            }

            return UserService.getPartnerSettings()
                .then(r => this.setPartnerSettings(r.data))
                .catch(e => storeHelper.handleError({ error: e, rethrow: false }))
                .finally(() => this.setPartnerSettingsLoaded());
        },
        loadEmployerCommunications (data: { employerId: number, portal: CommunicationPortal }) {
            this.setCommunicationsFetchState(FetchState.Loading);
            return UserService.loadEmployerCommunications(data.employerId, data.portal)
                .then(_ => this.setCommunications({ portal: data.portal, communications: _.data }))
                .catch(e => storeHelper.handleError({ error: e, rethrow: false }))
                .finally(() => this.setCommunicationsFetchState(FetchState.Loaded));
        },
        async saveUserEvent (data: any) {
            return UserService.saveUserEvent(data);
        },
        verifyIdpEmailChange (verifyIdpEmailChangeRequest: VerifyIdpEmailChangeRequest): Promise<any> {
            const notifierStore = useNotifierStore();
            return UserService.verifyIdpEmailChange(verifyIdpEmailChangeRequest)
                .then(_ => {
                    notifierStore.setSuccess("Your MFA email has been updated.");
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    return;
                });
        },
        createAdminUser (createAdminUserRequest: UserService.CreateAdminUserRequest): Promise<any> {
            // Call backend to create user in db and idp as well as record their userAccess records
            return UserService.createAdminUser(createAdminUserRequest)
            .then(resp => {
                if (resp.data) {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Admin User Create Request successful.");
                    return resp.data;
                }
            })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        updateAdminUser (updateAdminUserRequest: UserService.UpdateAdminUserRequest): Promise<any> {
            // Call backend to update user in db and idp as well as update their userAccess records
            return UserService.updateAdminUser(updateAdminUserRequest)
            .then(resp => {
                if (resp.data) {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Admin User Update successful.");
                    return resp.data;
                }
            })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        activateAdminUser (statusChange: AdminUserStatusChangeRequest): Promise<any> {
            // Call backend to activate user status in okta
            return UserService.activeAdminUser(statusChange)
            .then(resp => {
                if (resp.data) {
                    this.setCurrentAdminPortalUser(resp.data);
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Activated Admin User successfully.");
                    return resp.data;
                }
            })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        deactivateAdminUser (statusChange: AdminUserStatusChangeRequest): Promise<any> {
            // Call backend to deactivate user status in okta
            return UserService.deactivateAdminUser(statusChange)
                .then(resp => {
                    this.setCurrentAdminPortalUser(resp.data);
                    if (resp.data) {
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess("Deactivated Admin User successfully.");
                        return resp.data;
                    }
            })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        resetPasswordAdminUser (statusChange: AdminUserStatusChangeRequest): Promise<any> {
            // Call backend to deactivate user status in okta
            return UserService.resetPasswordAdminUser(statusChange)
                .then(resp => {
                    this.setCurrentAdminPortalUser(resp.data);
                    if (resp.data) {
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess("Password reset email sent successfully.");
                        return resp.data;
                    }
            })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        unlockAdminUser (statusChange: AdminUserStatusChangeRequest): Promise<any> {
            // Call backend to unlock user status in okta
            return UserService.unlockAdminUser(statusChange)
                .then(resp => {
                    this.setCurrentAdminPortalUser(resp.data);
                    if (resp.data) {
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess("Unlocked Admin User successfully.");
                        return resp.data;
                    }
            })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        resendActivationEmail (statusChange: AdminUserStatusChangeRequest): Promise<any> {
            // Call backend to Resend activation email for the user
            return UserService.resendActivationEmail(statusChange)
            .then(resp => {
                if (resp.data) {
                    this.setCurrentAdminPortalUser(resp.data);
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Activation email resent successfully.");
                    return resp.data;
                }
            })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        getAdminUsers (): Promise<any> {
            // Call backend to create user in db and idp as well as record their userAccess records
            return UserService.getAdminPortalUsers()
            .then(r => this.setAdminPortalUsers(r.data))
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        setAdminPortalUsers (users: AdminPortalUser[]) {
            this.adminPortalUsers = users;
        },
        searchAdminPortalUsers (request: SearchAdminUsers): Promise<any> {
            this.setAdminPortalUserSearchFetchState(FetchState.Loading);
            return UserService.searchAdminPortalUsers(request)
            .then(r => {
                this.setAdminPortalUserResults(r.data);
                this.setAdminPortalUserSearchFetchState(FetchState.Loaded);
            })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        syncAdminUser (userId: number): Promise<any> {
            // Call backend to sync user records
            return UserService.syncAdminUser(userId)
                .then(resp => {
                    if (resp.data) {
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess("Admin User Sync successful.");
                        return resp.data;
                    }
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    return;
                });
        },
        setAdminPortalUserResults (response: AdminUserResponse) {
            response.totalPages =   Math.ceil(response.total / response.pageSize);

            response.pageRange = _.range(response.pageNumber - 2, response.pageNumber + 3)
            .filter(page => page > 0 && page <= response.totalPages);

            this.adminPortalUserResult = response;

        },
        setAdminPortalUserSearchFetchState (fetchState: FetchState) {
            this.adminPortalUserSearchFetchState = fetchState;
        },
        setCurrentAdminPortalUser (user: AdminPortalUser) {
            this.currentAdminPortalUser = user;
        }
    }
});